import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    finalizeImprovementPlan,
    getImprovementPlanFinalizeForm,
    getImprovementPlanFinalizeOptions,
    getNumberOfPendingActions
} from "../../api/improvementPlanAPI";
import { ImprovementPlanFinalizeDto, ImprovementPlanObjectiveFinalizeDto } from "../../types";
import { ObjectiveFinalStatusEnum } from "../../types/enums";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { getImprovementPlanFinalizeSchema } from "../../schema/improvementPlanFinalizeSchema";
import "./ImprovementPlanFinalizeModal.css";
import { formatCurrency } from "../../../../utils/formatters/CurrencyFormatter";

type ImprovementPlanFinalizeModalProps = {
    show: boolean;
    improvementPlanId: number | null;
    onClose: () => void;
};


export default function ImprovementPlanFinalizeModal({
    show,
    improvementPlanId,
    onClose,
}: ImprovementPlanFinalizeModalProps) {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
    const [reasonOptions, setReasonOptions] = useState<SelectItemDto[]>([]);
    const [formData, setFormData] = useState<ImprovementPlanFinalizeDto | null>(null);
    const [showPendingActionsConfirm, setShowPendingActionsConfirm] = useState(false);
    const [pendingActionsCount, setPendingActionsCount] = useState(0);

    useEffect(() => {
        if (!show || !improvementPlanId) {
            return;
        }

        const loadData = async () => {
            setIsLoading(true);
            setError(undefined);
            setValidationErrors({});
            setShowPendingActionsConfirm(false);
            setPendingActionsCount(0);

            try {
                const [finalizeForm, options] = await Promise.all([
                    getImprovementPlanFinalizeForm(improvementPlanId),
                    getImprovementPlanFinalizeOptions()
                ]);

                setFormData(finalizeForm);
                setReasonOptions(options);
            } catch (loadError) {
                console.log(loadError);
                setError(t("common:requestError.errorBadRequest"));
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [show, improvementPlanId, t]);

    const updateObjectiveStatus = (objectiveId: number, finalStatus: number) => {
        const objectiveIndex = formData?.objectives.findIndex((x) => x.id === objectiveId) ?? -1;

        setFormData((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                objectives: prev.objectives.map((objective) =>
                    objective.id === objectiveId
                        ? { ...objective, finalStatus }
                        : objective
                )
            };
        });
        setValidationErrors((prev) => {
            const next = { ...prev };
            if (objectiveIndex >= 0) {
                delete next[`objectives[${objectiveIndex}].finalStatus`];
            }
            return next;
        });
    };

    const handleFinalizeImprovementPlan = async () => {
        if (!formData || !improvementPlanId) {
            return;
        }

        setIsSaving(true);
        setError(undefined);

        try {
            await finalizeImprovementPlan(improvementPlanId, formData);
            onClose();
            navigate("/improvement-plans", {
                state: { message: t("improvementPlan:modal.messageEndedImprovementPlanSuccessfully"), shouldRefresh: true }
            });
        } catch (saveError) {
            console.log(saveError);
            setError(t("common:requestError.errorBadRequest"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (!formData || !improvementPlanId) {
            return;
        }

        const schema = getImprovementPlanFinalizeSchema(t);

        try {
            await schema.validate(formData, { abortEarly: false });
            setValidationErrors({});
        } catch (validationError: any) {
            const errors: Record<string, string | undefined> = {};
            validationError.inner?.forEach((item: any) => {
                if (item.path) {
                    errors[item.path] = item.message;
                }
            });
            setValidationErrors(errors);
            return;
        }

        setIsSaving(true);
        setError(undefined);

        try {
            const pendingActions = await getNumberOfPendingActions(improvementPlanId);

            if (pendingActions > 0) {
                setPendingActionsCount(pendingActions);
                setShowPendingActionsConfirm(true);
                return;
            }

            await handleFinalizeImprovementPlan();
        } catch (saveError) {
            console.log(saveError);
            setError(t("common:requestError.errorBadRequest"));
        } finally {
            setIsSaving(false);
        }
    };

    const objectives = formData?.objectives ?? [];
    const objectiveFinalStatusOptions = Object.keys(ObjectiveFinalStatusEnum)
        .filter((key) => Number.isNaN(Number(key)))
        .map((statusName) => ({
            statusName,
            value: ObjectiveFinalStatusEnum[statusName as keyof typeof ObjectiveFinalStatusEnum]
        }));

    return (
        <Modal show={show} onHide={onClose} centered className="improvement-plan-finalize-modal" size="xl">
            <Modal.Header closeButton>
                <Modal.Title>{t("improvementPlan:modal.finalize.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form>
                        <Row>
                            <Col xs={12}>
                                <h5 className="mb-1">{t("improvementPlan:modal.finalize.reasonTitle")}</h5>
                                <Form.Group>
                                    <Form.Label>{t("improvementPlan:modal.finalize.reasonLabel")}</Form.Label>
                                    <Form.Select
                                        value={formData?.finalizeReasonId ?? ""}
                                        isInvalid={!!validationErrors.finalizeReasonId}
                                        onChange={(event) => {
                                            setFormData((prev) => {
                                                if (!prev) {
                                                    return prev;
                                                }

                                                return {
                                                    ...prev,
                                                    finalizeReasonId: event.target.value ? Number(event.target.value) : 0
                                                };
                                            });
                                            setValidationErrors((prev) => {
                                                const next = { ...prev };
                                                delete next.finalizeReasonId;
                                                return next;
                                            });
                                        }}
                                    >
                                        <option value="">{t("common:selectDefaultText")}</option>
                                        {reasonOptions.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.finalizeReasonId}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col xs={12}>
                                    <h5 className="mb-1">{t("improvementPlan:modal.finalize.objectivesTitle")}</h5>
                            </Col>
                        </Row>
                        {objectives.length > 0 ? <>
                            <Row className="mb-2">
                                <Col xs={8}>
                                    <Form.Label>{t("improvementPlan:modal.finalize.objectiveDescriptionLabel")}</Form.Label>
                                </Col>
                                <Col xs={4}>
                                    <Form.Label>{t("improvementPlan:modal.finalize.objectiveAchievementLabel")}</Form.Label>
                                </Col>
                            </Row>
                            {objectives.map((objective: ImprovementPlanObjectiveFinalizeDto, index) => (
                                <Row key={objective.id} className="mb-2 align-items-center">
                                    <Col xs={8}>
                                        <Form.Control value={objective.description || ""} readOnly />
                                    </Col>
                                    <Col xs={4}>
                                        <Form.Select
                                            value={objective.finalStatus ?? ""}
                                            isInvalid={!!validationErrors[`objectives[${index}].finalStatus`]}
                                            onChange={(event) =>
                                                updateObjectiveStatus(objective.id, Number(event.target.value))
                                            }
                                        >
                                            <option value="">{t("common:selectDefaultText")}</option>
                                            {objectiveFinalStatusOptions.map((statusOption) => (
                                                <option key={statusOption.statusName} value={statusOption.value}>
                                                    {t(`improvementPlan:modal.finalize.status.${statusOption.statusName}`)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors[`objectives[${index}].finalStatus`]}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Row>
                            ))}
                        </> : (
                            <Row>
                                <Col xs={12}>
                                    <span>{t("improvementPlan:modal.finalize.noObjectives")}</span>
                                </Col>
                            </Row>
                        )}

                        <Row className="mt-4">
                            <Col xs={12}>
                                <h5 className="mb-1">{t("improvementPlan:modal.finalize.economicDataTitle")}</h5>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={4} className="mb-2 mb-md-0">
                                <Form.Label>{t("improvementPlan:form.fields.estimatedInverstment")}:</Form.Label>
                                <Form.Control value={formatCurrency(formData?.estimatedInverstment)} readOnly />
                            </Col>
                            <Col xs={12} md={4} className="mb-2 mb-md-0">
                                <Form.Label>{t("improvementPlan:form.fields.returnedInverstment")}:</Form.Label>
                                <Form.Control value={formatCurrency(formData?.returnedInverstment)} readOnly />
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Label>{t("improvementPlan:modal.finalize.realizedInvestmentLabel")}:</Form.Label>
                                <Form.Control
                                    type="number"
                                    inputMode="decimal"
                                    value={formData?.finalInverstment ?? ""}
                                    onChange={(event) =>
                                        setFormData((prev) => (prev ? {
                                            ...prev,
                                            finalInverstment: event.target.value ? Number(event.target.value) : undefined
                                        } : prev))
                                    }
                                />
                            </Col>
                        </Row>
                        {error && (
                            <Row className="mt-3">
                                <Col xs={12}>
                                    <h6 className="text-danger">{error}</h6>
                                </Col>
                            </Row>
                        )}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-success" onClick={onClose} disabled={isSaving || isLoading}>
                    {t("common:buttons.buttonCancel")}
                </Button>
                <Button variant="success" onClick={handleSave} disabled={isSaving || isLoading}>
                    {isSaving && (
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                    )}
                    {t("common:buttons.buttonEnd")}
                </Button>
            </Modal.Footer>

            <Modal show={showPendingActionsConfirm} onHide={() => { setShowPendingActionsConfirm(false); onClose(); }} centered>
                <Modal.Body>
                    {t("improvementPlan:modal.finalize.pendingActionsMessage", { count: pendingActionsCount })}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => {
                            setShowPendingActionsConfirm(false);
                            onClose();
                        }}
                        disabled={isSaving}
                    >
                        {t("common:buttons.buttonNo")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={async () => {
                            setShowPendingActionsConfirm(false);
                            await handleFinalizeImprovementPlan();
                        }}
                        disabled={isSaving}
                    >
                        {isSaving && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                        )}
                        {t("common:buttons.buttonYes")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
}
